import { useEffect, useRef } from "react";
import {
  WORLDS_SWITCHER_APPS,
  worldsAppDestination,
  type WorldsAppLocation,
} from "./appSwitcher";

interface AppSwitcherDialogProps {
  location: WorldsAppLocation;
  onClose: () => void;
}

export function AppSwitcherDialog({ location, onClose }: AppSwitcherDialogProps) {
  const dialogRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;

      const focusableElements = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>("a[href], button:not([disabled])") ?? [],
      );
      const firstElement = focusableElements.at(0);
      const lastElement = focusableElements.at(-1);
      if (!firstElement || !lastElement) return;

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [onClose]);

  return (
    <div
      className="modal-backdrop worlds-app-switcher-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={dialogRef}
        className="worlds-app-switcher-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="worlds-app-switcher-title"
        aria-describedby="worlds-app-switcher-description"
      >
        <header>
          <div>
            <span>Centience apps</span>
            <h2 id="worlds-app-switcher-title">Switch workspace</h2>
          </div>
          <button ref={closeButtonRef} type="button" onClick={onClose} aria-label="Close app switcher">×</button>
        </header>
        <p id="worlds-app-switcher-description" className="worlds-app-switcher-description">
          Move between Centience products.
        </p>
        <nav aria-label="Centience apps">
          <ul className="worlds-app-switcher-list">
            {WORLDS_SWITCHER_APPS.map((app) => {
              const isCurrent = app.id === "worlds";
              return (
                <li key={app.id}>
                  <a
                    className={`worlds-app-switcher-option${isCurrent ? " current" : ""}`}
                    href={worldsAppDestination(app.id, location)}
                    aria-current={isCurrent ? "page" : undefined}
                  >
                    <span className="worlds-app-switcher-monogram" aria-hidden="true">{app.monogram}</span>
                    <span className="worlds-app-switcher-copy">
                      <b>Centience {app.name}</b>
                      <small>{app.description}</small>
                    </span>
                    {isCurrent
                      ? <span className="worlds-app-switcher-current">Current</span>
                      : <span className="worlds-app-switcher-arrow" aria-hidden="true">→</span>}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
      </section>
    </div>
  );
}
